import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Oval } from "react-loader-spinner";
import ActionTooltip from "components/ActionTooltip";
import Modal from "components/modal";
import removeLogoIcon from "images/userDetails/removeLogo.svg";
import useNotify from "hooks/useNotify";
import { actions } from "store/actions";

function ProfileSection({
  icon: Icon,
  iconSrc,
  title,
  description,
  onChangeLogo,
  isActive = false,
  showChangeLogoButton = true,
  changeLogoButtonText,
  logoFormatText,
  children,
}) {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const user = useSelector((state) => state.user || {});
  const isVendor = !user?.isAgency;
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmRemoveLogo = () => {
    setIsDeleting(true);
    dispatch(
      actions.deleteUserLogo((success, err) => {
        setIsDeleting(false);
        if (success) {
          notifySuccess(t("logoRemovedSuccessfully"));
          setIsRemoveOpen(false);
        } else {
          notifyError(
            err?.response?.data?.message ||
              err?.message ||
              t("actionFailed") ||
              "Failed to remove logo"
          );
        }
      })
    );
  };

  const [isUploading, setIsUploading] = useState(false);
  const { notifyError, notifySuccess } = useNotify();
  const userDetails = useSelector((state) => state.userDetails || {});

  const handleChangeLogo = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      notifyError(
        t("invalidFileType") || "Please upload only JPEG, PNG, or GIF files"
      );
      return;
    }

    setIsUploading(true);

    // Dispatch saga to upload via FormData to /user/logo
    dispatch(
      actions.uploadUserLogo(file, (success, err) => {
        setIsUploading(false);
        if (success) {
          notifySuccess(
            t("logoUploadedSuccessfully") || "Logo uploaded successfully"
          );
        } else {
          notifyError(
            err?.response?.data?.message ||
              err?.message ||
              t("uploadFailed") ||
              "Failed to upload logo"
          );
        }
      })
    );

    // Reset file input
    e.target.value = null;
  };

  return (
    <div className="flex flex-1 p-[24px] flex-col gap-[30px]">
      {/* Title */}
      <div className="flex items-center gap-[12px]">
        <div className="text-[#1D1B22] font-bold text-[38px]">
          {title || t("profileSectionTitle")}
        </div>
        {isActive && (
          <div className="flex py-[4px] px-[8px] items-center justify-center gap-[2px] rounded-[6px] border border-[#8FD9A2] bg-[#E0F4E3] mt-[2px]">
            <div className="text-[#1FA14A] text-center font-semibold text-[14px]">
              {t("Active")}
            </div>
          </div>
        )}
      </div>
      {/* Icon and Change Logo Section */}
      <div className="flex items-center gap-[24px]">
        {/* Icon Display */}
        <div className="w-[132px] h-[132px] flex items-center justify-center relative group">
          {userDetails?.logo ? (
            <>
              <img
                src={userDetails.logo}
                alt={title || "Profile logo"}
                className="w-full h-full object-contain rounded-[96px] "
              />

              {!isUploading && (
                <div className="absolute top-2 right-2 hidden group-hover:flex">
                  <button
                    type="button"
                    onClick={() => setIsRemoveOpen(true)}
                    className="flex items-center justify-center w-[132px] h-[132px] rounded-full shadow "
                  >
                    <img src={removeLogoIcon} alt="Remove Logo" />
                  </button>
                </div>
              )}
            </>
          ) : Icon ? (
            <Icon className="w-full h-full" />
          ) : iconSrc ? (
            <img
              src={iconSrc}
              alt={title || "Profile icon"}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="w-full h-full bg-[#F4F4F5] rounded-[8px]" />
          )}
          {isUploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-[8px] flex items-center justify-center">
              <Oval
                height={40}
                width={40}
                color="#023A66"
                visible={true}
                ariaLabel="oval-loading"
                secondaryColor="#fff"
                strokeWidth={4}
                strokeWidthSecondary={3}
              />
            </div>
          )}
        </div>

        {showChangeLogoButton && (
          <div className="flex flex-col gap-[10px]">
            <button
              onClick={handleChangeLogo}
              className="flex h-[40px] px-[14px] justify-center items-center gap-[6px] rounded-[6px] border border-[#1E4A6B] cursor-pointer hover:bg-[#1E4A6B] hover:bg-opacity-5 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M9 10C10.1046 10 11 9.10457 11 8C11 6.89543 10.1046 6 9 6C7.89543 6 7 6.89543 7 8C7 9.10457 7.89543 10 9 10Z"
                  stroke="#1E4A6B"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M13 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22H15C20 22 22 20 22 15V10"
                  stroke="#1E4A6B"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M18 8V2L20 4"
                  stroke="#1E4A6B"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M18 2L16 4"
                  stroke="#1E4A6B"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M2.66992 18.95L7.59992 15.64C8.38992 15.11 9.52992 15.17 10.2399 15.78L10.5699 16.07C11.3499 16.74 12.6099 16.74 13.3899 16.07L17.5499 12.5C18.3299 11.83 19.5899 11.83 20.3699 12.5L21.9999 13.9"
                  stroke="#1E4A6B"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span className="text-[#1E4A6B] font-semibold text-[16px]">
                {isVendor ? t("changeProfilePicture") : t("changeLogo")}
              </span>
            </button>

            <div className="text-[#1D1B2266] text-[16px] font-medium">
              {logoFormatText || t("logoFormat")}
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      {description && (
        <p className="text-[#70707B] font-normal text-[14px] leading-[20px]">
          {description}
        </p>
      )}

      {/* Children Content */}
      {children}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {/* Remove Logo Confirmation Modal */}
      <Modal isOpen={isRemoveOpen}>
        <div className="flex p-[32px] flex-col gap-[24px] w-[90%] max-w-[520px] bg-white rounded-[18px]">
          <div className="text-base-black font-bold text-[22px]">
            {isVendor ? t("removeProfilePic") : t("removeLogo")}
          </div>
          <div className="text-[#70707B] text-[14px]">
            {isVendor
              ? t("removeProfilePicConfirmMessage")
              : t("removeLogoConfirmMessage")}
          </div>
          <div className="flex justify-end gap-[12px] pt-[8px]">
            <button
              type="button"
              onClick={() => setIsRemoveOpen(false)}
              disabled={isDeleting}
              className="cursor-pointer flex py-[10px] px-[16px] items-center rounded-[6px] border border-default-color bg-white"
            >
              <span className="text-base-black font-bold text-[16px]">
                {t("Cancel")}
              </span>
            </button>
            <button
              type="button"
              onClick={handleConfirmRemoveLogo}
              disabled={isDeleting}
              className="cursor-pointer flex py-[10px] px-[16px] items-center gap-[8px] rounded-[6px] border border-primary bg-primary"
            >
              <Oval
                height={24}
                width={24}
                color="#FFF"
                visible={isDeleting}
                ariaLabel="oval-loading"
                secondaryColor="#FFF"
                strokeWidth={5}
                strokeWidthSecondary={3}
              />
              <span className="text-white font-bold text-[16px]">
                {t("Confirm")}
              </span>
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

ProfileSection.propTypes = {
  icon: PropTypes.elementType,
  iconSrc: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  onChangeLogo: PropTypes.func,
  showChangeLogoButton: PropTypes.bool,
  changeLogoButtonText: PropTypes.string,
  logoFormatText: PropTypes.string,
  children: PropTypes.node,
};

export default ProfileSection;
